import React, { useState, useEffect, useRef } from 'react';
import pb from '../../lib/pocketbase';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie, faPhone, faEnvelope, faHardHat, faUserEdit,
    faPlus, faSearch, faUserCircle, faShieldAlt, faTimes,
    faCamera, faSave, faUnlockAlt, faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import styles from '../Dashboard.module.css';

const Team = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState('list');

    const [avatarFile, setAvatarFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        try {
            setLoading(true);
            const internalRoles = ['admin', 'foreman', 'worker', 'employee'];
            const filterString = internalRoles.map(r => `role = "${r}"`).join(' || ');

            const records = await pb.collection('users').getFullList({
                sort: 'name',
                filter: `(${filterString})`
            });
            setTeamMembers(records);
        } catch (error) {
            console.error("Failed to fetch team:", error);
            toast.error("Erreur lors du chargement de l'équipe");
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (member) => {
        // Log to debug why phone might be missing
        console.log("Editing member data:", member);
        setEditingMember({ ...member });
        setAvatarFile(null);
        setPreviewUrl(member.avatar ? pb.files.getURL(member, member.avatar) : null);
        setIsEditModalOpen(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveMember = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const data = new FormData();
            data.append('name', editingMember.name);
            data.append('role', editingMember.role);
            if (editingMember.phone) {
                data.append('phone', editingMember.phone);
            }

            if (avatarFile) {
                data.append('avatar', avatarFile);
            }

            await pb.collection('users').update(editingMember.id, data);

            toast.success("Profil mis à jour !");
            setIsEditModalOpen(false);
            fetchTeam();
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setSaving(false);
        }
    };

    const handleInlineUpdate = async (userId, field, value) => {
        try {
            await pb.collection('users').update(userId, { [field]: value });
            toast.success("Mise à jour réussie");
            fetchTeam();
        } catch (error) {
            toast.error("Erreur de mise à jour");
        }
    };

    const handleResetPassword = async (email) => {
        if (!window.confirm(`Envoyer un email de réinitialisation à ${email} ?`)) return;
        try {
            await pb.collection('users').requestPasswordReset(email);
            toast.success(`Email envoyé !`);
        } catch (error) {
            toast.error("Erreur d'envoi");
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'admin': return 'Administrateur';
            case 'foreman': return 'Chef de Chantier';
            case 'worker': return 'Ouvrier';
            case 'employee': return 'Collaborateur';
            default: return role || 'Sans rôle';
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return faUserTie;
            case 'foreman': return faShieldAlt;
            case 'worker': return faHardHat;
            case 'employee': return faBriefcase;
            default: return faUserCircle;
        }
    };

    const filteredTeam = teamMembers.filter(member =>
        (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.role || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className={styles.loading}>Chargement...</div>;

    return (
        <div>
            <div className={styles.header}>
                <h2>Gestion de l'Équipe</h2>
                <div className={styles.headerActions}>
                    <div className={styles.viewToggles}>
                        <button
                            className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.active : ''}`}
                            onClick={() => setViewMode('list')}
                        >
                            Liste
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                            onClick={() => setViewMode('grid')}
                        >
                            Trombinoscope
                        </button>
                    </div>
                    <div className={styles.searchBar} style={{ position: 'relative' }}>
                        <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                paddingLeft: '35px',
                                borderRadius: '20px',
                                border: '1px solid #e2e8f0',
                                height: '40px',
                                width: '200px',
                                background: 'white'
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.dataSection}>
                {viewMode === 'list' ? (
                    <div className={styles.tableContainer}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Membre</th>
                                    <th>Poste / Rôle</th>
                                    <th>Contact</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTeam.map(member => (
                                    <tr key={member.id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                {member.avatar ? (
                                                    <img
                                                        src={pb.files.getURL(member, member.avatar)}
                                                        alt={member.name}
                                                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '40px', height: '40px', borderRadius: '50%',
                                                        background: 'var(--color-primary-light)', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)'
                                                    }}>
                                                        <FontAwesomeIcon icon={getRoleIcon(member.role)} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div style={{ fontWeight: '700' }}>{member.name || 'Sans nom'}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Inscrit le {new Date(member.created).toLocaleDateString()}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                value={member.role}
                                                onChange={(e) => handleInlineUpdate(member.id, 'role', e.target.value)}
                                                style={{
                                                    padding: '6px 28px 6px 14px',
                                                    borderRadius: '20px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700',
                                                    backgroundColor: member.role === 'admin' ? '#fee2e2' : 'var(--color-primary-light)',
                                                    color: member.role === 'admin' ? '#ef4444' : 'var(--color-primary)',
                                                    border: '1px solid transparent',
                                                    cursor: 'pointer',
                                                    outline: 'none',
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(member.role === 'admin' ? '#ef4444' : '#1e40af')}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 10px center',
                                                    backgroundSize: '12px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.filter = 'brightness(0.95)';
                                                    e.target.style.transform = 'translateY(-1px)';
                                                    e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.filter = 'none';
                                                    e.target.style.transform = 'none';
                                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                                }}
                                            >
                                                <option value="worker">Artisan Terrain</option>
                                                <option value="foreman">Chef de Chantier</option>
                                                <option value="employee">Bureau</option>
                                                <option value="admin">Administrateur</option>
                                            </select>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span style={{ fontSize: '0.85rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                                                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', opacity: 0.6 }} />
                                                    {member.email || "Email masqué"}
                                                </span>
                                                {member.phone && (
                                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                        <FontAwesomeIcon icon={faPhone} style={{ marginRight: '6px', opacity: 0.6 }} />
                                                        {member.phone}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className={styles.actions}>
                                            <button onClick={() => handleEditClick(member)} title="Modifier le profil" style={{ color: 'var(--color-primary)' }}>
                                                <FontAwesomeIcon icon={faUserEdit} />
                                            </button>
                                            <button onClick={() => handleResetPassword(member.email)} title="Reset Password" style={{ color: '#64748b' }}>
                                                <FontAwesomeIcon icon={faUnlockAlt} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                        {filteredTeam.map(member => (
                            <div key={member.id} className={styles.card} style={{ flexDirection: 'column', textAlign: 'center', padding: '30px' }}>
                                {member.avatar ? (
                                    <img
                                        src={pb.files.getURL(member, member.avatar)}
                                        alt={member.name}
                                        style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '15px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{
                                        width: '80px', height: '80px', borderRadius: '50%',
                                        background: 'var(--color-primary-light)', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)',
                                        fontSize: '2rem', marginBottom: '15px', margin: '0 auto 15px'
                                    }}>
                                        <FontAwesomeIcon icon={getRoleIcon(member.role)} />
                                    </div>
                                )}
                                <h3 style={{ margin: '0 0 5px' }}>{member.name}</h3>
                                <select
                                    value={member.role}
                                    onChange={(e) => handleInlineUpdate(member.id, 'role', e.target.value)}
                                    style={{
                                        padding: '6px 28px 6px 14px',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        backgroundColor: member.role === 'admin' ? '#fee2e2' : 'var(--color-primary-light)',
                                        color: member.role === 'admin' ? '#ef4444' : 'var(--color-primary)',
                                        border: '1px solid transparent',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(member.role === 'admin' ? '#ef4444' : '#1e40af')}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 10px center',
                                        backgroundSize: '12px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        margin: '5px auto'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.filter = 'brightness(0.95)';
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.filter = 'none';
                                        e.target.style.transform = 'none';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                    }}
                                >
                                    <option value="worker">Équipe Terrain (Chantiers / Planning)</option>
                                    <option value="foreman">Équipe Terrain (Responsable / Planning)</option>
                                    <option value="employee">Gestion & Bureau</option>
                                    <option value="admin">Super-Administrateur</option>
                                </select>
                                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                                        <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px', opacity: 0.5 }} />
                                        {member.email}
                                    </div>
                                    {member.phone && (
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                            <FontAwesomeIcon icon={faPhone} style={{ marginRight: '8px', opacity: 0.5 }} />
                                            {member.phone}
                                        </div>
                                    )}
                                </div>
                                <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                    <button onClick={() => handleEditClick(member)} className={styles.viewBtn}>Modifier Profil</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* EDIT MODAL */}
            {isEditModalOpen && editingMember && (
                <div className={styles.modalOverlay} onClick={() => setIsEditModalOpen(false)}>
                    <div className={styles.messageModal} style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitle}>
                                <FontAwesomeIcon icon={faUserEdit} />
                                <h3>Modifier le Profil</h3>
                            </div>
                            <button className={styles.closeModalBtn} onClick={() => setIsEditModalOpen(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveMember}>
                            <div className={styles.modalBody}>
                                {/* Photo Section */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                    {previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '4px solid var(--color-primary-light)', boxShadow: 'var(--shadow-md)' }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100px', height: '100px', borderRadius: '50%',
                                            background: 'var(--color-light-background)', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem',
                                            color: 'var(--color-primary)', border: '4px solid var(--color-primary-light)'
                                        }}>
                                            <FontAwesomeIcon icon={getRoleIcon(editingMember.role)} />
                                        </div>
                                    )}
                                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
                                    <button type="button" className={styles.viewBtn} onClick={() => fileInputRef.current.click()} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FontAwesomeIcon icon={faCamera} /> Changer la photo
                                    </button>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b' }}>Nom Complet</label>
                                        <input
                                            type="text"
                                            value={editingMember.name || ''}
                                            onChange={e => setEditingMember({ ...editingMember, name: e.target.value })}
                                            style={{ padding: '10px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b' }}>Poste / Rôle</label>
                                        <select
                                            value={editingMember.role || 'worker'}
                                            onChange={e => setEditingMember({ ...editingMember, role: e.target.value })}
                                            style={{ padding: '10px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white' }}
                                        >
                                            <option value="worker">Équipe Terrain (Chantiers / Planning)</option>
                                            <option value="foreman">Équipe Terrain (Responsable / Planning)</option>
                                            <option value="employee">Gestion & Bureau</option>
                                            <option value="admin">Super-Administrateur</option>
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b' }}>Téléphone</label>
                                        <input
                                            type="text"
                                            value={editingMember.phone || editingMember.telephone || ''}
                                            placeholder="Numéro de téléphone"
                                            onChange={e => setEditingMember({ ...editingMember, phone: e.target.value })}
                                            style={{ padding: '10px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b' }}>Email</label>
                                        <input
                                            type="email"
                                            value={editingMember.email || ''}
                                            disabled
                                            style={{ padding: '10px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', opacity: 0.7 }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.modalFooter} style={{ gap: '10px' }}>
                                <button type="button" className={styles.viewBtn} onClick={() => setIsEditModalOpen(false)}>Annuler</button>
                                <button type="submit" className={styles.addButton} disabled={saving}>
                                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Team;
